// src/features/stock-ranking/StockRankingPage.tsx
//
// Equities ranking feature page. Loads the per-sector factor aggregates
// from GET /api/v1/ranking/sector-summary once (drives the radar + the
// full sector-filter pill list), and the ranked cross-section from
// GET /api/v1/ranking/universe. The universe is fetched SERVER-SIDE per
// sector via the ?sector= param: selecting "All Sectors" loads the full
// ~220-symbol payload, selecting a sector pill refetches just that
// sector's rows. Sorting then happens client-side on whatever set is
// loaded. A single batched call to GET /api/v1/ranking/historical-batch
// powers the table sparklines for the currently-visible rows (no N+1
// waterfall). Reuses the standard <Layout sidebar={<Sidebar />}>
// dashboard shell so it sits behind the same auth gate / navigation as
// the rest of the suite.

import { useEffect, useMemo, useState } from 'react';

import { GlobalSearchBar } from '../global-search';
import { Layout, Sidebar } from '../../shared/ui';
import {
    fetchHistoricalBatch,
    fetchSectorSummary,
    fetchUniverse,
    type HistoricalBatchResponse,
    type SectorSummaryRow,
    type UniverseRow,
} from './api';
import { RiskReturnScatter } from './components/RiskReturnScatter';
import { SectorRadar } from './components/SectorRadar';
import { StockDetailPopup } from './components/StockDetailPopup';
import { UniverseTable, type SortKey } from './components/UniverseTable';

// Cap the batched sparkline fetch to the top N rows of the current
// sort/filter. This bounds the DB window query + payload regardless of
// how the user sorts, while still giving the most-relevant rows a trend.
const SPARKLINE_LIMIT = 60;

export const StockRankingPage = () => {
    const [universe, setUniverse] = useState<UniverseRow[]>([]);
    const [sectorSummary, setSectorSummary] = useState<SectorSummaryRow[]>([]);
    const [asOfDate, setAsOfDate] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isUniverseLoading, setIsUniverseLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [activeSector, setActiveSector] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('universe_rank');
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [hoverSymbol, setHoverSymbol] = useState<string | null>(null);
    // Ticker clicked in the table — drives the quick-stats pop-over.
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

    const [sparklines, setSparklines] = useState<HistoricalBatchResponse>({});

    // --- Sector aggregates: loaded ONCE on mount. -----------------------
    // Drives both the radar chart and the full filter-pill list, so the
    // pill list stays complete even when the universe is narrowed to a
    // single sector by the server-side ?sector= refetch below.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const sectorRes = await fetchSectorSummary();
                if (!cancelled) setSectorSummary(sectorRes.sectors);
            } catch (err) {
                // Non-fatal: the universe effect owns the page-level error
                // state. A missing radar should not blank the whole page.
                // eslint-disable-next-line no-console
                console.warn('Failed to load sector summary', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Universe: SERVER-SIDE refetch whenever the sector changes. -----
    // Selecting a pill hits GET /ranking/universe?sector=<name>; "All
    // Sectors" (activeSector === null) loads the full ~220-symbol payload.
    // This replaces client-side filtering so the table, scatter and
    // sparkline batch all operate on exactly the rows the server returned.
    useEffect(() => {
        let cancelled = false;
        setIsUniverseLoading(true);
        (async () => {
            try {
                const res = await fetchUniverse(activeSector ?? undefined);
                if (cancelled) return;
                setUniverse(res.items);
                setAsOfDate(res.as_of_date);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load ranking universe', err);
                setError(
                    'Unable to load rankings right now. Please try again shortly.',
                );
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsUniverseLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeSector]);

    // --- Distinct sectors for the filter tabs (stable order). -----------
    // Sourced from the sector-summary aggregate (always the full set),
    // NOT from the universe — which may hold a single sector's rows after
    // a server-side filter and would otherwise collapse the pill list.
    const sectors = useMemo(
        () =>
            sectorSummary
                .map((s) => s.sector)
                .sort((a, b) => a.localeCompare(b)),
        [sectorSummary],
    );

    // Full universe size = sum of per-sector counts. Stable regardless of
    // which sector the universe payload is currently filtered to, so the
    // "All Sectors" pill always shows the true total (e.g. 220).
    const totalUniverseSize = useMemo(
        () => sectorSummary.reduce((acc, s) => acc + (s.count ?? 0), 0),
        [sectorSummary],
    );

    // --- Client-side SORT only (filtering is server-side now). ----------
    const processed = useMemo(() => {
        const sorted = [...universe].sort((a, b) => {
            const va = a[sortKey];
            const vb = b[sortKey];
            let cmp: number;
            if (typeof va === 'string' && typeof vb === 'string') {
                cmp = va.localeCompare(vb);
            } else {
                cmp = (va as number) - (vb as number);
            }
            return sortDesc ? -cmp : cmp;
        });
        return sorted;
    }, [universe, sortKey, sortDesc]);

    // --- Symbols whose sparklines we batch-fetch (top of current view). -
    const sparklineSymbols = useMemo(
        () => processed.slice(0, SPARKLINE_LIMIT).map((r) => r.symbol),
        [processed],
    );
    const sparklineSymbolSet = useMemo(
        () => new Set(sparklineSymbols),
        [sparklineSymbols],
    );

    // --- Batched sparkline fetch, refreshed when the visible set changes.
    useEffect(() => {
        if (sparklineSymbols.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchHistoricalBatch(sparklineSymbols, 90);
                if (cancelled) return;
                // Merge so previously-loaded symbols persist when the
                // visible window shifts (avoids a flash of empty trends).
                setSparklines((prev) => ({ ...prev, ...res }));
            } catch (err) {
                // Sparklines are non-critical; never surface as a page error.
                // eslint-disable-next-line no-console
                console.warn('Failed to load sparkline history', err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Join key keeps the effect stable when the array identity changes
        // but the contents don't.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sparklineSymbols.join(',')]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDesc((d) => !d);
        } else {
            setSortKey(key);
            // Rank/symbol/sector read best ascending; factor columns best
            // descending (strongest first).
            setSortDesc(key !== 'universe_rank' && key !== 'symbol' && key !== 'sector');
        }
    };

    const pillBase =
        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer';
    const pillActive =
        'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900';
    const pillInactive =
        'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700';

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Global Equity Rankings"
            subtitle="Cross-sectional multi-factor scores for the GICS 220 universe."
        >
            {/* Platform-wide asset search — jump to any deep-dive page. */}
            <div className="mb-8">
                <GlobalSearchBar />
            </div>

            {/* Header row: sector filter pills + freshness badge. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={`${pillBase} ${!activeSector ? pillActive : pillInactive}`}
                        onClick={() => setActiveSector(null)}
                    >
                        All Sectors
                        {totalUniverseSize > 0 && (
                            <span className="ml-1.5 opacity-70">
                                {totalUniverseSize}
                            </span>
                        )}
                    </button>
                    {sectors.map((s) => (
                        <button
                            key={s}
                            type="button"
                            className={`${pillBase} ${activeSector === s ? pillActive : pillInactive}`}
                            onClick={() => setActiveSector(s)}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                {asOfDate && (
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-2 py-1 rounded">
                        Updated: {asOfDate}
                    </span>
                )}
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
                    Loading quantitative data…
                </div>
            ) : (
                <>
                    {/* Charts: cross-sectional Risk/Return + sector radar. */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Risk vs Return
                            </h2>
                            <p className="muted text-xs mb-4">
                                {activeSector ?? 'All sectors'} ·{' '}
                                {processed.length} names
                            </p>
                            <RiskReturnScatter
                                data={processed}
                                highlightSymbol={hoverSymbol}
                            />
                        </div>
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Sector Factor Radar
                            </h2>
                            <p className="muted text-xs mb-4">
                                Average z-scores by GICS sector
                            </p>
                            <SectorRadar sectors={sectorSummary} />
                        </div>
                    </div>

                    {/* Full sortable universe table. Dimmed while a
                        server-side sector refetch is in flight so the
                        sector switch reads as a deliberate reload. */}
                    <div
                        className={`transition-opacity ${isUniverseLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
                        aria-busy={isUniverseLoading}
                    >
                        <UniverseTable
                            data={processed}
                            sparklines={sparklines}
                            sparklineSymbols={sparklineSymbolSet}
                            sortKey={sortKey}
                            sortDesc={sortDesc}
                            onSort={handleSort}
                            onRowHover={setHoverSymbol}
                            onSymbolClick={setSelectedSymbol}
                        />
                    </div>
                </>
            )}

            {/* Professional tabbed detail pop-over for the clicked ticker. */}
            {selectedSymbol && (
                <StockDetailPopup
                    symbol={selectedSymbol}
                    onClose={() => setSelectedSymbol(null)}
                />
            )}
        </Layout>
    );
};

export default StockRankingPage;
